<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import DatePicker from '~/components/form-elements/DatePicker.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import BrandSelect from '~/components/form-elements/select/BrandSelect.vue'
import SupplierSelect from '~/components/form-elements/select/SupplierSelect.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import { useExporting } from '~/composables/exporting'
import AuthScope from '~/enums/auth-scope'
import Export from '~/enums/export'
import ExportPeriod from '~/enums/export-period'
import type { DateTimeRange } from '~/types/date-time'
import type { RadioButtonChoice, SelectedItem } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  authScope: AuthScope
}>()

type Data = {
  brandId?: string
  exportPeriod: ExportPeriod
  orderCreatedAfter?: string
  orderCreatedBefore?: string
  supplierId?: string
  timeZone?: string
}

type Form = {
  brand: SelectedItem | null
  dateTimeRange: DateTimeRange | null
  exportPeriod: ExportPeriod
  supplier: SelectedItem | null
}

const exportPeriodChoices: RadioButtonChoice[] = [
  ...ExportPeriod.sortedMembers.map(
    (x: ExportPeriod): RadioButtonChoice => ({ labelText: ExportPeriod.getName(x), value: x })
  ),
  { labelText: 'Дата / диапазон дат', value: null }
]

const { exportData } = useExporting()

const form = ref<Form>({
  brand: null,
  dateTimeRange: null,
  exportPeriod: ExportPeriod.CurrentMonthInMsk,
  supplier: null
})
const isExporting = ref(false)

const filename = computed(() => {
  const { exportPeriod } = form.value
  const period = exportPeriod ? ExportPeriod.getFilenamePostfix(exportPeriod)! : null
  const postfix = period ? ` ${period}` : ''

  return `Продажи${postfix}.xlsx`
})

function close (): void {
  model.value = false
}

async function download (): Promise<void> {
  const { brand, dateTimeRange, exportPeriod, supplier } = form.value

  const data: Data = { exportPeriod }

  if (exportPeriod === null && dateTimeRange) {
    const { formattedDateTime1, formattedDateTime2, timeZone } = dateTimeRange
    data.orderCreatedAfter = formattedDateTime1
    data.orderCreatedBefore = formattedDateTime2
    data.timeZone = timeZone
  }

  if (props.authScope === AuthScope.Admin) {
    if (brand) {
      data.brandId = brand.id
    }

    if (supplier) {
      data.supplierId = supplier.id
    }
  }

  isExporting.value = true

  await exportData(props.authScope, Export.SalesStatistics, filename.value, data)

  isExporting.value = false
}

function setDateTimeRange (x: DateTimeRange | null): void {
  form.value.dateTimeRange = x
}
</script>

<template>
  <CustomModalWindow v-model="model">
    <template #title>
      Экспорт статистики продаж
    </template>

    <template v-if="authScope === AuthScope.Admin">
      <BrandSelect v-model="form.brand" :auth-scope="authScope" class="mb-6" />

      <SupplierSelect v-model="form.supplier" class="mb-6" />
    </template>

    <RadioButtons
      v-model="form.exportPeriod"
      :choices="exportPeriodChoices"
      class="mb-6"
      is-vertical
      label-text="Период создания заказов (МСК)"
      name="exportPeriod"
    />

    <DatePicker
      v-if="form.exportPeriod === null"
      class="mb-6"
      is-range-mode
      @set-date-time-range="setDateTimeRange"
    />

    <BasicButton color="green" :is-loading="isExporting" title="Скачать таблицу (XLSX)" @click="download" />

    <template #action>
      <a href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
